import { inject, Injectable } from '@angular/core';
import { KeychainAccess, SecureStorage } from '@aparajita/capacitor-secure-storage';
import { Platform } from '@ionic/angular';

const MAX_RETRIES = 30;
const RETRY_DELAY_MS = 500;

@Injectable({
  providedIn: 'root',
})
export class SecureStorageService {
  private readonly platform = inject(Platform);

  async init(): Promise<void> {
    console.log('🔐 Secure storage init starts');

    await SecureStorage.setSynchronize(false);
    await SecureStorage.setDefaultKeychainAccess(KeychainAccess.afterFirstUnlockThisDeviceOnly);
    await SecureStorage.setKeyPrefix('dinkClubWeb');

    await this.waitUntilReady();

    console.log('🔐 Secure storage initialized');
  }

  async setItem(key: string, value: string): Promise<void> {
    await SecureStorage.set(key, value);
  }

  async getItem(key: string): Promise<string | null> {
    try {
      return (await SecureStorage.get(key)) as string;
    } catch {
      return null;
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      await SecureStorage.remove(key);
    } catch {
      // ignore — key may not exist
    }
  }

  async clear(): Promise<void> {
    await SecureStorage.clear();
  }

  async keys(): Promise<string[]> {
    try {
      return await SecureStorage.keys();
    } catch {
      return [];
    }
  }

  getPlatformSecurity(): string {
    if (this.platform.is('ios')) return 'iOS Keychain (高安全性)';
    if (this.platform.is('android')) return 'Android Keystore (高安全性)';
    return 'Web localStorage (低安全性)';
  }

  private async waitUntilReady(): Promise<void> {
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        await this.setItem('testKey', 'testValue');
        const value = await this.getItem('testKey');
        if (value !== 'testValue') throw new Error('Value mismatch');
        await this.removeItem('testKey');
        return;
      } catch {
        if (attempt === MAX_RETRIES) throw new Error('Secure storage failed to initialise after max retries');
        console.warn(`Secure storage not ready, retrying… (${attempt}/${MAX_RETRIES})`);
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }
  }
}
